import { type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';
import { formatMoney, formatMoneyInput, parseMoneyInput, type MoneyInputIssue } from '@vello/format';
import { Field } from './forms';
import { TextInput } from './primitives';

/**
 * `MoneyInput` (W1-ADMIN, D358) — the one control an operator uses to type an amount of money. The operator types
 * **major units** in their own decimal convention (`12,50` or `12.50`); the control emits **integer minor units** of
 * the given ISO 4217 currency (via `@vello/format` `parseMoneyInput`, which reads the currency's minor-unit exponent
 * and the locale separators), so no screen ever asks a human to type cents again.
 *
 * Neutral by construction: the currency and the locale are props (the operating market decides them), the copy for a
 * refusal comes from the app's catalog through `issueText`, and the only visual values are tokens. Accessibility: a real
 * `<label>`, `inputMode="decimal"`, tabular numerals, `aria-invalid` + an `aria-describedby` error once the operator
 * leaves the field, and a polite live preview of how the value was read (e.g. "€ 1.234,50") so the interpretation is
 * visible before submit. The 48px floor comes from `TextInput`; the label / hint / error rows are a `Field` (D508), so a
 * `MoneyInput` aligns in a `FormRow` like any other field.
 */
export interface MoneyInputProps {
  readonly id: string;
  readonly label: string;
  /** ISO 4217 alpha-3 currency the amount is in. */
  readonly currency: string;
  /** BCP-47 locale for the decimal separator and the preview. */
  readonly locale: string;
  /** The committed value in integer minor units, or undefined when empty/invalid. */
  readonly valueMinor: bigint | undefined;
  readonly onChangeMinor: (value: bigint | undefined) => void;
  /** Localized text for a refusal code (the app's catalog owns the copy). */
  readonly issueText: (issue: MoneyInputIssue) => string;
  readonly hint?: ReactNode;
  readonly required?: boolean;
  readonly disabled?: boolean;
  /** Inclusive ceiling in minor units. */
  readonly maxMinor?: bigint;
}

export function MoneyInput({ id, label, currency, locale, valueMinor, onChangeMinor, issueText, hint, required = false, disabled = false, maxMinor }: MoneyInputProps): ReactElement {
  const [text, setText] = useState<string>(() => (valueMinor !== undefined ? formatMoneyInput(valueMinor, currency, locale) : ''));
  const [touched, setTouched] = useState(false);
  // The last value this control emitted, so an external change (a form reset, a loaded record) re-syncs the text
  // without fighting the operator's in-progress typing.
  const emitted = useRef<bigint | undefined>(valueMinor);
  const context = useRef({ currency, locale });

  useEffect(() => {
    if (valueMinor !== emitted.current) {
      emitted.current = valueMinor;
      context.current = { currency, locale };
      setText(valueMinor !== undefined ? formatMoneyInput(valueMinor, currency, locale) : '');
      setTouched(false);
      return;
    }
    if (context.current.currency === currency && context.current.locale === locale) return;
    context.current = { currency, locale };
    // The currency or locale changed under the same text: a value can never silently keep another currency's exponent.
    if (text === '' && valueMinor !== undefined) {
      setText(formatMoneyInput(valueMinor, currency, locale));
      return;
    }
    const r = parseMoneyInput(text, currency, locale, maxMinor !== undefined ? { maxMinor } : {});
    const value = r.ok ? r.amountMinor : undefined;
    if (value !== emitted.current) {
      emitted.current = value;
      onChangeMinor(value);
    }
    // `text`, `maxMinor` and `onChangeMinor` are read at the moment the currency/locale changes; re-running on every
    // keystroke would re-emit what `onText` already emitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueMinor, currency, locale]);

  const parsed = parseMoneyInput(text, currency, locale, maxMinor !== undefined ? { maxMinor } : {});
  const issue: MoneyInputIssue | undefined = parsed.ok ? undefined : parsed.issue === 'EMPTY' && !required ? undefined : parsed.issue;
  const showIssue = touched && issue !== undefined;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const previewId = `${id}-preview`;
  const preview = parsed.ok ? safeFormat(parsed.amountMinor, currency, locale) : undefined;
  const describedBy = [hint !== undefined ? hintId : undefined, showIssue ? errorId : undefined, preview !== undefined ? previewId : undefined].filter((v) => v !== undefined).join(' ');

  const onText = (next: string): void => {
    setText(next);
    const r = parseMoneyInput(next, currency, locale, maxMinor !== undefined ? { maxMinor } : {});
    const value = r.ok ? r.amountMinor : undefined;
    if (value !== emitted.current) {
      emitted.current = value;
      onChangeMinor(value);
    }
  };

  return (
    <Field
      label={label}
      htmlFor={id}
      {...(hint !== undefined ? { hint } : {})}
      {...(showIssue ? { error: issueText(issue) } : {})}
      footer={
        preview !== undefined ? (
          <span id={previewId} className="tnum" aria-live="polite" style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-caption-size)' }}>
            {preview}
          </span>
        ) : undefined
      }
    >
      <div style={{ position: 'relative' }}>
        <TextInput
          id={id}
          className="tnum"
          inputMode="decimal"
          autoComplete="off"
          value={text}
          required={required}
          disabled={disabled}
          aria-invalid={showIssue}
          aria-describedby={describedBy !== '' ? describedBy : undefined}
          onChange={(e) => onText(e.target.value)}
          onBlur={() => setTouched(true)}
          style={{ paddingRight: 'var(--space-12)' }}
        />
        <span aria-hidden="true" className="tnum" style={{ position: 'absolute', right: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-label-size)', pointerEvents: 'none' }}>
          {currency}
        </span>
      </div>
    </Field>
  );
}

function safeFormat(amountMinor: bigint, currency: string, locale: string): string | undefined {
  try {
    return formatMoney({ amountMinor, currency }, { locale, timeZone: 'UTC', measurement: 'metric' });
  } catch {
    return undefined;
  }
}
